import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import MuiAvatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ImagePreviewLightbox from '../components/common/uploadCommon/ImagePreviewLightbox';

export interface DescriptionsProps {
  title?: React.ReactNode;
  bordered?: boolean;
  column?: number;
  size?: 'default' | 'middle' | 'small';
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

export interface DescriptionsItemProps {
  label?: React.ReactNode;
  children?: React.ReactNode;
  span?: number;
  [key: string]: any;
}

const DescriptionsItem: React.FC<DescriptionsItemProps> = () => null;

const DescriptionsRoot: React.FC<DescriptionsProps> & { Item: typeof DescriptionsItem } = ({
  title,
  bordered,
  column = 3,
  style,
  className,
  children,
}) => {
  const items: DescriptionsItemProps[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === DescriptionsItem) {
      items.push(child.props as DescriptionsItemProps);
    }
  });

  return (
    <Box className={className} sx={style}>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${column}, 1fr)`,
          border: bordered ? '1px solid #f0f0f0' : 'none',
        }}
      >
        {items.map((item, i) => (
          <React.Fragment key={i}>
            <Box
              sx={{
                p: 1,
                bgcolor: bordered ? '#fafafa' : 'transparent',
                border: bordered ? '1px solid #f0f0f0' : 'none',
                fontWeight: 500,
                gridColumn: item.span ? `span ${Math.min(item.span, column)}` : undefined,
              }}
            >
              {item.label}
            </Box>
            <Box
              sx={{
                p: 1,
                border: bordered ? '1px solid #f0f0f0' : 'none',
                gridColumn: item.span ? `span ${Math.min(item.span, column)}` : undefined,
              }}
            >
              {item.children}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

DescriptionsRoot.Item = DescriptionsItem;
export const Descriptions = DescriptionsRoot;

export interface ResultProps {
  status?: 'success' | 'error' | 'info' | 'warning' | '403' | '404' | '500';
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  extra?: React.ReactNode;
  icon?: React.ReactNode;
  style?: React.CSSProperties;
  [key: string]: any;
}

export const Result: React.FC<ResultProps> = ({ status = 'info', title, subTitle, extra, icon, style }) => (
  <Box sx={{ textAlign: 'center', py: 6, ...style }}>
    {icon ?? <ErrorOutlineIcon sx={{ fontSize: 72, color: status === '403' ? 'error.main' : 'text.secondary', mb: 2 }} />}
    {title && <Typography variant="h5" sx={{ mb: 1 }}>{title}</Typography>}
    {subTitle && <Typography color="text.secondary" sx={{ mb: 3 }}>{subTitle}</Typography>}
    {extra && <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>{extra}</Box>}
  </Box>
);

export interface ProgressProps {
  percent?: number;
  status?: 'success' | 'exception' | 'active' | 'normal';
  showInfo?: boolean;
  strokeWidth?: number;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: any;
}

export const Progress: React.FC<ProgressProps> = ({ percent = 0, showInfo = true, style, className }) => (
  <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 1, ...style }}>
    <Box sx={{ flex: 1 }}>
      <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, percent))} />
    </Box>
    {showInfo && <Typography variant="body2">{percent}%</Typography>}
  </Box>
);

export interface CardProps {
  title?: React.ReactNode;
  extra?: React.ReactNode;
  bordered?: boolean;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  size?: 'default' | 'small';
  [key: string]: any;
}

export const Card: React.FC<CardProps> = ({ title, extra, bordered = true, style, className, children }) => (
  <MuiCard variant={bordered ? 'outlined' : 'elevation'} className={className} sx={style}>
    {(title || extra) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {extra}
      </Box>
    )}
    <CardContent>{children}</CardContent>
  </MuiCard>
);

export interface ImageProps {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  preview?: boolean | object;
  fallback?: string;
  onClick?: React.MouseEventHandler;
  [key: string]: any;
}

function collectImageSrcs(children: React.ReactNode): string[] {
  const srcs: string[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const src = (child.props as { src?: string })?.src;
    if (typeof src === 'string' && src) srcs.push(src);
  });
  return srcs;
}

const PreviewGroup: React.FC<{ preview?: any; children?: React.ReactNode; [key: string]: any }> = ({
  children,
  preview,
}) => {
  const visible = Boolean(preview?.visible);
  const onVisibleChange = preview?.onVisibleChange as ((vis: boolean) => void) | undefined;
  const srcs = useMemo(() => collectImageSrcs(children), [children]);

  return (
    <>
      {children}
      <ImagePreviewLightbox
        open={visible && srcs.length > 0}
        onClose={() => onVisibleChange?.(false)}
        images={srcs}
        index={0}
      />
    </>
  );
};

const ImageBase: React.FC<ImageProps> = ({ src, alt, width, height, style, fallback, onClick, ...rest }) => {
  void rest;
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      onError={(e: any) => {
        if (fallback) e.currentTarget.src = fallback;
      }}
      sx={{
        width,
        height,
        maxWidth: '100%',
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
    />
  );
};

export type ImageComponent = typeof ImageBase & { PreviewGroup: typeof PreviewGroup };

export const Image = ImageBase as ImageComponent;
Image.PreviewGroup = PreviewGroup;

export interface AvatarProps {
  src?: string;
  icon?: React.ReactNode;
  size?: number | 'large' | 'small' | 'default';
  style?: React.CSSProperties;
  children?: React.ReactNode;
  [key: string]: any;
}

export const Avatar: React.FC<AvatarProps> = ({ src, icon, size, style, children }) => {
  const sxSize =
    size === 'large' ? 40 : size === 'small' ? 24 : typeof size === 'number' ? size : 32;
  return (
    <MuiAvatar src={src} sx={{ width: sxSize, height: sxSize, ...style }}>
      {icon}
      {children}
    </MuiAvatar>
  );
};

export interface SpaceProps {
  size?: number | 'small' | 'middle' | 'large';
  direction?: 'horizontal' | 'vertical';
  wrap?: boolean;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

export const Space: React.FC<SpaceProps> = ({
  size = 'small',
  direction = 'horizontal',
  wrap,
  style,
  className,
  children,
}) => {
  const gap = size === 'large' ? 16 : size === 'middle' ? 12 : typeof size === 'number' ? size : 8;
  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: direction === 'vertical' ? 'column' : 'row',
        flexWrap: wrap ? 'wrap' : 'nowrap',
        gap: `${gap}px`,
        alignItems: direction === 'horizontal' ? 'center' : 'flex-start',
        ...style,
      }}
    >
      {children}
    </Box>
  );
};

const ListItemMeta: React.FC<{ title?: React.ReactNode; description?: React.ReactNode; avatar?: React.ReactNode }> = ({
  title,
  description,
  avatar,
}) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    {avatar}
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      <Typography variant="body2" color="text.secondary">{description}</Typography>
    </Box>
  </Box>
);

const ListItemWrapper: React.FC<{ children?: React.ReactNode; actions?: React.ReactNode[] }> = ({ children }) => (
  <ListItem>{children}</ListItem>
);

const ListRoot: React.FC<{
  dataSource?: any[];
  renderItem?: (item: any, index: number) => React.ReactNode;
  bordered?: boolean;
  style?: React.CSSProperties;
}> = ({ dataSource, renderItem, bordered, style }) => (
  <List sx={{ border: bordered ? '1px solid #f0f0f0' : 'none', ...style }}>
    {dataSource?.map((item, index) => (
      <React.Fragment key={item.key ?? index}>{renderItem?.(item, index)}</React.Fragment>
    ))}
  </List>
);

const ListWithSub: typeof ListRoot & {
  Item: typeof ListItemWrapper & { Meta: typeof ListItemMeta };
} = ListRoot as any;
ListWithSub.Item = Object.assign(ListItemWrapper, { Meta: ListItemMeta });
export const AntList = ListWithSub;
export { ListItemText };
